'use client'

import {ConnectedWallet, getAccessToken, usePrivy, useWallets} from "@privy-io/react-auth";
import React, {useEffect, useMemo, useRef, startTransition, useState} from "react";
import {useAppDispatch, useAppSelector} from "@/lib/redux/hooks";
import {clearAllNFTData, selectCache, setAllNFTData, setNFTData} from "@/lib/redux/slices/metaAnchorSlice";
import {Nft} from "@/lib/types";
import NftCard from "@/app/components/NftCard";
import {useRouter} from "next/navigation";
import LoadingCards from "@/app/components/LoadingCards";
import BuyAction from "@/app/components/BuyAction";

type WalletAddress = {
    address: string;
    connectedWallet?: ConnectedWallet
}

type Status = 'idle' | 'fetching' | 'done';

async function fetchNFTsForAddress(address: string): Promise<Nft[]> {
    const token = await getAccessToken();
    if (!token) return [];

    const response = await fetch('/api/meta-anchor/nfts', {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({address})
    });

    const result = await response.json();
    return result.success ? result.nfts : [];
}

export default function WalletOverview() {
    const {ready} = useWallets();
    const {user, unlinkWallet} = usePrivy();
    const router = useRouter();
    const dispatch = useAppDispatch();
    const cache = useAppSelector(selectCache);
    const fetchStatusRef = useRef<Status>('idle');
    const [selectedWallet, setSelectedWallet] = useState('');
    const [showDialog, setShowDialog] = useState(false);

    const smartWallet = useMemo(() => {
        if (!user) return null;
        const linkedAccounts = user.linkedAccounts;
        const smartWallets = linkedAccounts.filter(account => account.type === "smart_wallet");
        if (!smartWallets || smartWallets.length === 0) return null;
        return smartWallets[0];
    }, [user]);

    const metaMaskWallet = useMemo(() => {
        if (!user) return null;
        const linkedAccounts = user.linkedAccounts;
        const metaMaskWallet = linkedAccounts.filter(account => account.type === "wallet" && account.walletClientType === "metamask");
        if (!metaMaskWallet || metaMaskWallet.length === 0) return null;
        return metaMaskWallet[0];
    }, [user]);

    const allWalletAddresses = useMemo(() => {
        const addressesMap = new Map<string, WalletAddress>();

        if (smartWallet) {
            addressesMap.set(smartWallet.address.toLowerCase(), {address: smartWallet.address});
        }

        if (metaMaskWallet && "address" in metaMaskWallet) {
            addressesMap.set(metaMaskWallet.address.toLowerCase(), {address: metaMaskWallet.address});
        }

        return Array.from(addressesMap.values());
    }, [smartWallet, metaMaskWallet]);

    useEffect(() => {
        if (!ready || !user || fetchStatusRef.current !== 'idle') return;
        if (allWalletAddresses.length === 0) {
            return;
        }

        // clear data first
        dispatch(clearAllNFTData());

        fetchStatusRef.current = 'fetching';

        const fetchAllData = async () => {
            const results = await Promise.all(
                allWalletAddresses.map(async ({address}: WalletAddress) => {
                    const nfts = await fetchNFTsForAddress(address);
                    return {address, nfts};
                })
            );

            results.forEach(({address, nfts}) => {
                dispatch(setAllNFTData({walletAddress: address, nfts}));
            });

            fetchStatusRef.current = 'done';
        };

        fetchAllData().then(() => {
            // nothing
        });
    }, [ready, user, allWalletAddresses, dispatch]);

    const hasData = Object.keys(cache).length > 0;
    const isLoading = !ready || (!hasData && !!user);

    const routeToMetaMaskNft = (caip: string) => {
        startTransition(() => {
            router.push(`/nft/metamask/${encodeURIComponent(caip)}`, {scroll: false});
        })
    }

    const routeToNft = (caip: string) => {
        startTransition(() => {
            router.push(`/nft/${encodeURIComponent(caip)}`, {scroll: false});
        })
    }

    const handleUnlinkWallet = async () => {
        if (selectedWallet) {
            await unlinkWallet(selectedWallet);
            setShowDialog(false);
            setSelectedWallet('');
        }
    }

    const handleOpenUnlink = (address: string) => {
        setSelectedWallet(address);
        setShowDialog(true);
    };

    return (
        <div>
            {isLoading ? (
                <LoadingCards/>
            ) : (
                allWalletAddresses.map(({address, connectedWallet}) => {
                    const data = cache[address];
                    const isMetaMaskNft = connectedWallet?.meta?.id === 'io.metamask' ||
                        user?.linkedAccounts.some(account =>
                            account.type === 'wallet' &&
                            account.address.toLowerCase() === address.toLowerCase() &&
                            account.walletClientType === 'metamask'
                        );
                    if (isMetaMaskNft) {
                        return (
                            <div key={address} className="mb-6">
                                <div className="mb-3 text-right">
                                    <button
                                        onClick={() => handleOpenUnlink(address)}
                                        className="text-[#E24756] text-[10px] pt-0.75 uppercase">
                                        <span>Unlink Wallet</span>
                                    </button>
                                </div>
                                {!data || !data.nfts || data.nfts.length === 0 ? (
                                    <p className="font-neutra text-sm text-grey-02">No NFTs</p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3">
                                        {data.nfts.map((nft, index) => (
                                            <div
                                                onClick={() => routeToMetaMaskNft(nft.caip)}
                                                key={index}>
                                                <NftCard
                                                    key={nft.caip}
                                                    nft={nft}
                                                    isMetaMaskNft={isMetaMaskNft}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    } else {
                        return (
                            <div key={address} className="mb-6">
                                {!data || !data.nfts || data.nfts.length === 0 ? (
                                    <div className="relative">
                                        <LoadingCards/>
                                        <div className="absolute top-3 left-0 right-0">
                                            <div className="flex flex-col justify-center items-center mt-10">
                                                <p className="font-neutra text-[24px] text-white mb-3">KEINE WEINE
                                                    GEFUNDEN</p>
                                                <BuyAction/>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-2 gap-3">
                                            {data.nfts.map((nft, index) => (
                                                <div
                                                    onClick={() => routeToNft(nft.caip)}
                                                    key={index}>
                                                    <NftCard
                                                        key={nft.caip}
                                                        nft={nft}
                                                        isMetaMaskNft={isMetaMaskNft}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-7">
                                            <BuyAction/>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    }
                })
            )}

            {showDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div className="bg-dark-04 border border-grey-02 rounded-[10px] p-6 w-full max-w-md">
                        <h3 className="font-neutra text-xl text-white mb-4 uppercase">Unlink Wallet</h3>
                        <p className="font-neutra font-light text-[18px] text-white mb-4">
                            Wollen Sie dieses Wallet ({selectedWallet.slice(0, 6)}...{selectedWallet.slice(-4)})
                            wirklich von Ihrem Account entfernen?
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowDialog(false);
                                    setSelectedWallet('');
                                }}
                                className="px-4 py-2 font-neutra text-sm rounded-md border border-grey-02 text-white hover:bg-dark-05 transition-colors"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={handleUnlinkWallet}
                                className="px-4 py-2 font-neutra text-sm rounded-md bg-gold text-white">
                                Bestätigen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
